import React, {
    useState, useRef, useEffect, useCallback,
} from 'react'
import debounce from 'lodash/debounce'
import { Spin } from 'antd'
import API from '../../../services/API'

/**
 * Autocomplete multiselección con Checkboxes, buscando productos reales via API.
 *
 * El universo de productos disponibles para buscar se restringe según lo que se
 * eligió en el paso "Condición" del descuento (apply_to / items_to_apply):
 * - all_products: busca en todos los productos de la base de datos.
 * - specific_products: solo entre los productos elegidos en ese paso.
 * - specific_brands: solo entre productos de esas marcas.
 * - specific_product_types: solo entre productos de esos tipos de producto.
 *
 * @param {Array} selected - Array de productos seleccionados actualmente [{ id, name, price }, ...]
 * @param {Function} onChange - Función callback que retorna el nuevo array de productos seleccionados
 * @param {Boolean} disabled
 * @param {Object|String} applyTo - Valor (o field de antd { value }) de apply_to del paso "Condición"
 * @param {Object|Array} itemsToApply - Valor (o field de antd { value }) de items_to_apply del paso "Condición"
 * @param {Array} excludedProductIds - Ids de productos ya elegidos en otros bloques de descuento personalizado, para no ofrecerlos aquí también
 */
const ProductAutocomplete = ({
    selected = [], onChange, disabled = false, applyTo, itemsToApply, excludedProductIds = [],
}) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [products, setProducts] = useState([])
    const [fetching, setFetching] = useState(false)
    const containerRef = useRef(null)

    const selectedIds = selected.map((p) => p.id)
    const visibleProducts = products.filter((p) => !excludedProductIds.includes(p.id))

    const applyToValue = (applyTo && applyTo.value !== undefined) ? applyTo.value : applyTo
    const restrictedItems = ((itemsToApply && itemsToApply.value !== undefined) ? itemsToApply.value : itemsToApply) || []
    const isRestrictedToProducts = applyToValue === 'specific_products'

    // Buscar productos reales en el API (o filtrar localmente si se restringió a productos específicos)
    const fetchProducts = useCallback(debounce((value) => {
        if (isRestrictedToProducts) {
            const restrictedProducts = restrictedItems.map((item) => ({
                id: item.key !== undefined ? item.key : item.id,
                name: item.label || item.name,
                price: item.price,
            }))
            setProducts(value
                ? restrictedProducts.filter((p) => p.name && p.name.toLowerCase().includes(value.toLowerCase()))
                : restrictedProducts)
            setFetching(false)
            return
        }

        if (!value || value.length < 2) {
            setProducts([])
            setFetching(false)
            return
        }
        setFetching(true)
        let uri = `/api/discounts/products-search?filter[q]=${value}`
        if (applyToValue === 'specific_brands' && restrictedItems.length > 0) {
            uri += `&${restrictedItems.map((item) => `filter[brand_id][]=${item.key !== undefined ? item.key : item.id}`).join('&')}`
        }
        if (applyToValue === 'specific_product_types' && restrictedItems.length > 0) {
            uri += `&${restrictedItems.map((item) => `filter[product_type_id][]=${item.key !== undefined ? item.key : item.id}`).join('&')}`
        }
        API.get(uri).then((response) => {
            setProducts(response.data.data.map((item) => ({
                id: item.id,
                name: item.name,
                price: item.price,
            })))
            setFetching(false)
        }).catch(() => {
            setFetching(false)
        })
    }, 500), [applyToValue, isRestrictedToProducts, JSON.stringify(restrictedItems)])

    useEffect(() => {
        fetchProducts(searchTerm)
    }, [searchTerm, fetchProducts])

    // Cerrar el menú desplegable si el usuario hace clic fuera del componente.
    // Arrastrar la barra de scroll de la página también dispara un "mousedown" en el
    // document (fuera del contenedor), lo que cerraba el dropdown y perdía el foco del
    // input al intentar hacer scroll mientras se buscaba. clientX/clientY fuera de
    // document.documentElement.clientWidth/clientHeight indica que el clic cayó en la
    // barra de scroll nativa (que queda fuera del viewport "de contenido"), no en la página.
    useEffect(() => {
        const handleClickOutside = (event) => {
            const clickedOnScrollbar = event.clientX >= document.documentElement.clientWidth
                || event.clientY >= document.documentElement.clientHeight
            if (clickedOnScrollbar) return

            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Alternar selección de un producto individual
    const handleToggleProduct = (product) => {
        const isSelected = selectedIds.includes(product.id)
        let updatedSelected;

        if (isSelected) {
            updatedSelected = selected.filter((p) => p.id !== product.id)
        } else {
            updatedSelected = [...selected, product]
        }

        if (onChange) onChange(updatedSelected)
    }

    // Seleccionar o desmarcar todos los visibles en la búsqueda
    const handleSelectAllFiltered = () => {
        const allFilteredAreSelected = visibleProducts.length > 0 && visibleProducts.every((p) => selectedIds.includes(p.id))

        if (allFilteredAreSelected) {
            const filteredIds = visibleProducts.map((p) => p.id)
            onChange(selected.filter((p) => !filteredIds.includes(p.id)))
        } else {
            const notYetSelected = visibleProducts.filter((p) => !selectedIds.includes(p.id))
            onChange([...selected, ...notYetSelected])
        }
    }

    const renderDropdownContent = () => {
        if (fetching) {
            return (
                <div style={{ padding: '12px', textAlign: 'center' }}>
                    <Spin size="small" />
                </div>
            )
        }

        if (visibleProducts.length === 0) {
            const emptyMessage = (!isRestrictedToProducts && searchTerm.length < 2)
                ? 'Escribe al menos 2 letras para buscar.'
                : 'No se encontraron productos.'
            const emptyStyle = {
                padding: '12px', textAlign: 'center', color: '#888', fontSize: '0.85rem',
            }
            return (
                <div style={emptyStyle}>
                    {emptyMessage}
                </div>
            )
        }

        return (
            <>
                {/* Botón rápido para marcar/desmarcar visibles */}
                <button
                    type="button"
                    onClick={handleSelectAllFiltered}
                    style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        border: 'none',
                        padding: '8px 12px',
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        color: '#0066cc',
                        fontWeight: '600',
                        backgroundColor: '#f8f9fa',
                        userSelect: 'none',
                    }}
                >
                    {visibleProducts.every((p) => selectedIds.includes(p.id))
                        ? 'Desmarcar todos los visibles'
                        : 'Seleccionar todos los visibles'}
                </button>

                {/* Lista de Ítems */}
                {visibleProducts.map((product) => {
                    const isChecked = selectedIds.includes(product.id)
                    const inputId = `product-autocomplete-${product.id}`
                    return (
                        <label
                            key={product.id}
                            htmlFor={inputId}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '9px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f4f4f4',
                                backgroundColor: isChecked ? '#eef6ff' : '#ffffff',
                                userSelect: 'none',
                                transition: 'background 0.15s ease',
                            }}
                        >
                            <input
                                id={inputId}
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleToggleProduct(product)}
                                style={{ marginRight: '10px', cursor: 'pointer' }}
                            />
                            <span style={{ flexGrow: 1, fontSize: '0.9rem', color: '#333' }}>
                                {product.name}
                            </span>
                            {product.price && (
                                <span style={{ fontSize: '0.8rem', color: '#777' }}>
                                    {`$${product.price}`}
                                </span>
                            )}
                        </label>
                    )
                })}
            </>
        )
    }

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%', maxWidth: '420px' }}>
            {/* Input del Autocomplete */}
            <input
                type="text"
                placeholder="Escribe para buscar (ej. Libreta)..."
                value={searchTerm}
                disabled={disabled}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsOpen(true)}
                style={{
                    width: '100%',
                    padding: '10px 12px',
                    boxSizing: 'border-box',
                    borderRadius: '6px',
                    border: '1px solid #ccc',
                    outline: 'none',
                    fontSize: '0.95rem',
                }}
            />

            {/* Dropdown flotante con Checkboxes */}
            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px',
                        maxHeight: '230px',
                        overflowY: 'auto',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 1000,
                    }}
                >
                    {renderDropdownContent()}
                </div>
            )}
        </div>
    )
}

export default ProductAutocomplete
